import '../components/ErrorCatalogue.css';

import { Link, useParams } from 'react-router-dom';

import ErrorCatalogueContent from '../components/ErrorCatalogueContent';
import ErrorNav from '../components/ErrorNav';
import { useErrorCatalogues } from '../query/errors';

/**
 * The error pages: permanent, unversioned, publicly readable.
 *
 *   /errors                      — which catalogues exist
 *   /errors/{catalogue}          — that catalogue's codes
 *   /errors/{catalogue}/{code}   — one code; this is what an RFC 9457 `type` URI
 *                                  in a live API response points at
 *
 * No version appears anywhere in these URLs, and none ever will: a `type` URI
 * is a permanent contract token, so it must keep resolving long after the
 * release that introduced it — and long after the service stops emitting the
 * code, which is why a page that is no longer published is marked retired
 * rather than removed.
 *
 * These routes are outside ProtectedRoute on purpose — the reader is an
 * anonymous integration partner who followed a link out of a failing call.
 *
 * The index sidebar is mounted here rather than inside ErrorCatalogueContent so
 * that the content component stays reusable without dragging a second
 * navigation tree along with it.
 */
export default function ErrorsPage() {
  const { service, code } = useParams<{ service?: string; code?: string }>();

  return (
    <div className="errors-layout">
      <ErrorNav activeCatalogue={service} activeCode={code} />
      <div className="errors-main">
        {service ? <ErrorCatalogueContent catalogueId={service} code={code} /> : <ErrorsIndex />}
      </div>
    </div>
  );
}

function ErrorsIndex() {
  // The catalogues on record — not the registered APIs. A service appears here
  // once it has published a catalogue, and `common` appears because codes were
  // filed under it, not because anything owns it.
  const cataloguesQuery = useErrorCatalogues();

  return (
    <div className="errors">
      <nav className="errors-crumbs">
        <Link to="/">SkyMap Docs</Link>
        <span>/</span>
        <span>Errors</span>
      </nav>

      <h1 className="errors-title">Error catalogues</h1>
      <p className="errors-meta">
        Documentation for the <code>type</code> URIs carried by SkyMap error responses.
      </p>

      <div className="errors-intro">
        <p>
          Each SkyMap service publishes the error codes it can return. Codes whose meaning is fixed
          platform-wide — the ones derived from JSON Schema keywords and HTTP status phrases — live
          under <code className="md-code">/errors/common</code> instead, because no single service
          owns them.
        </p>
        <p>
          Pages here are permanent. A code that a service stops returning is kept and marked
          retired, so a <code className="md-code">type</code> URI in a response you are holding
          keeps resolving.
        </p>
      </div>

      {cataloguesQuery.isPending && <p className="errors-empty">Loading catalogues…</p>}

      {cataloguesQuery.isError && (
        <div className="errors-notice errors-notice--warning">
          Could not load the error catalogues.
        </div>
      )}

      {cataloguesQuery.isSuccess && cataloguesQuery.data.length === 0 && (
        <p className="errors-empty">No service has published an error catalogue yet.</p>
      )}

      {cataloguesQuery.isSuccess && cataloguesQuery.data.length > 0 && (
        <div className="errors-list">
          {cataloguesQuery.data.map((catalogue) => (
            <div className="errors-card" key={catalogue.catalogueId}>
              <div className="errors-card-head">
                <Link className="errors-code" to={`/errors/${catalogue.catalogueId}`}>
                  /errors/{catalogue.catalogueId}
                </Link>
                <div className="errors-badges">
                  <span className="errors-badge errors-badge--level">
                    {catalogue.codeCount} code{catalogue.codeCount === 1 ? '' : 's'}
                  </span>
                  {catalogue.retiredCount > 0 && (
                    <span
                      className="errors-badge errors-badge--retired"
                      title="Kept so older type URIs keep resolving"
                    >
                      {catalogue.retiredCount} retired
                    </span>
                  )}
                </div>
              </div>
              <p className="errors-card-title">{catalogue.displayName}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
